import React, { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageProvider';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { motion } from 'framer-motion';
import { FallbackUI } from './FallbackUI';

export const StatsSection: React.FC = () => {
  const { t } = useLanguage();
  const { ref, isVisible } = useScrollReveal();
  const [counts, setCounts] = useState<number[]>([]);

  const items = t.stats.items;

  useEffect(() => {
    if (isVisible && items && items.length > 0) {
      const targets = items.map(item => {
        const num = parseInt(item.value.replace(/\D/g, ''));
        return isNaN(num) ? 0 : num;
      });

      setCounts(targets.map(() => 0));

      const duration = 2000;
      const steps = 60;
      const interval = duration / steps;
      let step = 0;

      const timer = setInterval(() => {
        step++;
        const progress = step / steps;
        const eased = 1 - Math.pow(1 - progress, 3);

        setCounts(targets.map(target => Math.round(target * eased)));

        if (step >= steps) {
          clearInterval(timer);
          setCounts(targets);
        }
      }, interval);

      return () => clearInterval(timer);
    }
  }, [isVisible, items]);

  if (!items || items.length === 0) {
    return <FallbackUI message="Statistics loading..." />;
  }

  const formatValue = (index: number) => {
    const original = items[index].value;
    const num = counts[index] || 0;
    if (original.includes('+')) return `${num}+`;
    if (original.includes('h')) return `${num}h`;
    if (original.includes('★')) return `${num}★`;
    return `${num}`;
  };

  return (
    <section id="stats" className="relative py-24 md:py-32 bg-vilarias-black">
      <div className="absolute inset-0 bg-hero-glow opacity-40" />

      <div className="section-padding relative z-10" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-serif text-3xl md:text-5xl text-white mb-4">
            {t.stats.headline}
          </h2>
          <div className="h-px w-16 bg-vilarias-accent mx-auto" />
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {items.map((item, index) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.12 }}
              className="text-center"
            >
              <div className="glass-card rounded-2xl p-8 hover:border-vilarias-accent/30 transition-all duration-300">
                <p className="text-4xl md:text-5xl font-serif text-white mb-2 text-glow">
                  {formatValue(index)}
                </p>
                <p className="text-sm text-vilarias-text-secondary tracking-wide uppercase">
                  {item.label}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
